import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ProductCard from "@/components/ProductCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useSeo from "@/lib/useSeo";
import NumInput from "@/components/NumInput";

function PriceInput({ k, ph, params, set }) {
  const [v, setV] = useState(Number(params.get(k) || 0));
  return (
    <NumInput value={v} onChange={setV} placeholder={ph} onBlur={() => set(k, v ? String(v) : "")} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      data-testid={`filter-${k.replace("_", "-")}`} className="w-full border border-[#E6DFD5] px-3 py-2 bg-white" />
  );
}

const TYPES = ["physical", "digital", "course", "workshop"];

function Filters({ params, set, cols }) {
  const { t, pick } = useApp();
  const types = (params.get("type") || "").split(",").filter(Boolean);
  const toggleType = (ty) => set("type", (types.includes(ty) ? types.filter((x) => x !== ty) : [...types, ty]).join(","));
  return (
    <aside className="space-y-8 text-sm" data-testid="shop-filters">
      <div className="space-y-3">
        <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t("type").toUpperCase()}</p>
        {TYPES.map((ty) => (
          <label key={ty} className="flex items-center gap-3 cursor-pointer">
            <Checkbox checked={types.includes(ty)} onCheckedChange={() => toggleType(ty)} data-testid={`filter-type-${ty}`} />{t(ty)}
          </label>
        ))}
      </div>
      <div className="space-y-3">
        <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t("category").toUpperCase()}</p>
        <Select value={params.get("category") || "all"} onValueChange={(v) => set("category", v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-none" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            {cols.map((c) => <SelectItem key={c.slug} value={c.slug}>{pick(c, "title")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-3">
        <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t("price").toUpperCase()} (IDR)</p>
        <div className="flex gap-2">
          <PriceInput k="min_price" ph="Min" params={params} set={set} />
          <PriceInput k="max_price" ph="Max" params={params} set={set} />
        </div>
      </div>
      <div className="space-y-3">
        <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t("rating").toUpperCase()}</p>
        <Select value={params.get("min_rating") || "0"} onValueChange={(v) => set("min_rating", v === "0" ? "" : v)}>
          <SelectTrigger className="rounded-none" data-testid="filter-rating"><SelectValue /></SelectTrigger>
          <SelectContent>{["0", "3", "4", "4.5"].map((r) => <SelectItem key={r} value={r}>{r === "0" ? t("all") : `${r}+`}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <label className="flex items-center justify-between">
        {t("availability")}
        <Switch checked={params.get("in_stock") === "true"} onCheckedChange={(v) => set("in_stock", v ? "true" : "")} data-testid="filter-in-stock" />
      </label>
    </aside>
  );
}

export default function Shop() {
  const { t } = useApp();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [cols, setCols] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  useSeo({ title: t("products"), description: "Shop handmade leather goods, digital patterns and workshops." });
  useEffect(() => { api.get("/collections").then((r) => setCols(r.data)); }, []);
  useEffect(() => {
    const p = Object.fromEntries(params.entries());
    api.get("/products", { params: p }).then((r) => setItems(r.data));
  }, [params]);
  const set = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };
  const q = params.get("q");
  const [term, setTerm] = useState(q || "");
  useEffect(() => { setTerm(q || ""); }, [q]);
  const submit = (e) => { e.preventDefault(); set("q", term.trim()); };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12" data-testid="shop-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl">{q ? `"${q}"` : t("products")}</h1>
          {items && <p className="text-sm text-[#6E6259] mt-2" data-testid="shop-result-count">{items.length} items</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={submit} className="flex border border-[#E6DFD5] bg-white" data-testid="shop-search-form">
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={t("search_ph")} className="px-3 py-2 text-sm outline-none w-48 sm:w-64 bg-transparent" data-testid="shop-search-input" />
            <button className="px-4 text-sm bg-[#3B220C] text-white" data-testid="shop-search-btn">{t("search")}</button>
          </form>
          <button className="lg:hidden flex items-center gap-2 border px-3 py-2 text-sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn"><SlidersHorizontal size={14} />{t("filters")}</button>
          <Select value={params.get("sort") || "newest"} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger className="w-48 rounded-none" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t("newest")}</SelectItem>
              <SelectItem value="price_asc">{t("price_asc")}</SelectItem>
              <SelectItem value="price_desc">{t("price_desc")}</SelectItem>
              <SelectItem value="rating">{t("top_rated")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid lg:grid-cols-[240px_1fr] gap-12">
        <div className={`${showFilters ? "block" : "hidden"} lg:block`}><Filters params={params} set={set} cols={cols} /></div>
        <div>
          {items && items.length === 0 && <p className="text-[#6E6259] py-20 text-center" data-testid="shop-empty">{t("no_results")}</p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-12">{(items || []).map((p, i) => <ProductCard key={p.product_id} p={p} i={i} />)}</div>
        </div>
      </div>
    </div>
  );
}
